import useApi from "@/hooks/useApi";
import useNetSuiteConfigs from "@/hooks/useNetSuiteConfigs";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import Select, { SingleValue } from "react-select";
import Option from "react-select/dist/declarations/src/components/Option";
import NetSuiteModal from "./NetSuiteModal";
import LoadingSpinner from "@/components/LoadingSpinner";
import NetSuiteInfoMessage from "./NetSuiteInfoMessage";
import NetSuitePanelRow from "./NetSuitePanelRow";
import { ChevronDown } from "@/components/ds/icons";
import { SecondaryButton, PrimaryButton, GhostButton } from "@/components/ds";
import CustomSelect from "./CustomSelect";
import { getMetaDataOptions } from "./MetaDataOptions";
import { Modal, ModalBody } from "@velaro/velaro-component-lib";
import { AlertTriangle, IconClose } from "@velaro/velaro-component-lib";
import { getRequiredFields } from "./NetsuiteRequiredFields";

export interface NetSuiteMap {
  id: number;
  netSuiteField: SingleValue<Option>;
  label: string;
  linkedTo: string;
  create: boolean;
  usedForSearching: boolean;
  showWhenSelected: boolean;
  showInSearch: boolean;
  control: string;
  defaultValue: string;
  recordType: string;
  isRequired?: boolean;
}

interface Option {
  value: any;
  label: string;
  key?: string;
  disabled?: boolean;
}

interface NetSuiteConfig {
  id: number;
  displayName: string;
  company: string;
  teamId: number;
}

interface Props {
  teamId: number;
  metadataType: string;
  showSavedMessage: () => void;
  isCreateMode?: boolean;
  selectedConfigId: number;
  isDeleteDisabled: boolean;
}

interface NetSuiteListItem {
  value: number;
  text: string;
}

export interface NetSuiteList {
  netSuiteField: string;
  listItems: Option[];
}

export default function NetsuitePanel(props: Props) {
  const [mappedFields, setMappedFields] = useState<NetSuiteMap[]>([]);
  const [metadataType, setMetadataType] = useState<string>(props.metadataType);
  const [selectedConfig, setSelectedConfig] = useState<number | undefined>(
    undefined
  );
  const [metadata, setMetadata] = useState<Option[]>([]);
  const [isLoading, setisLoading] = useState(false);
  const [isListLoading, setIsListLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [passedField, setPassedField] = useState<NetSuiteMap>();
  const { items, loading } = useNetSuiteConfigs(props.teamId);
  const [listFields, setListFields] = useState<NetSuiteList[] | null>(null);
  const api = useApi();
  const [expanded, setExpanded] = useState(true);
  const metaDataOptions = getMetaDataOptions(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [recordTypeToDelete, setRecordTypeToDelete] = useState<string | null>(
    null
  );
  const [isDeleteFieldModalOpen, setIsDeleteFieldModalOpen] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState<{
    index: number;
    fieldName: string;
  } | null>(null);
  const [isHydratingState, setIsHydratingState] = useState(false);
  const [metadataRefreshCounter, setMetadataRefreshCounter] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);

  /**
   * "Sync from NetSuite" — invalidates the server-side field metadata cache
   * (24h Redis) and refetches. Use after admins add custom fields in NetSuite
   * and want them surfaced in the mapper without waiting for cache expiry.
   * Backed by POST /NetSuite/Integrations/FieldDefinitions/Refresh/{id}/{recordType}.
   */
  const syncFieldsFromNetSuite = async () => {
    if (!metadataType || !props.selectedConfigId) return;
    setSyncing(true);
    setSyncError(null);
    try {
      const recordType =
        metadataType === "Ticket" ? "SupportCase"
        : metadataType === "Order" ? "Order"
        : metadataType;
      const resp = await api.messaging.post(
        `NetSuite/Integrations/FieldDefinitions/Refresh/${props.selectedConfigId}/${recordType}`,
        {}
      );
      if (!resp.ok) {
        const body = await resp.text().catch(() => "");
        throw new Error(`HTTP ${resp.status}: ${body.slice(0, 200)}`);
      }
      setLastSyncedAt(new Date());
      setMetadataRefreshCounter((c) => c + 1);
    } catch (e) {
      setSyncError(e instanceof Error ? e.message : String(e));
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    if (!loading) {
      setSelectedConfig(items[0]?.id);
    }
  }, [items, loading]);

  useEffect(() => {
    async function fetchMetadata() {
      //This is the REST method of getting metadata
      //Currently, NetSuite does not support this for all record types, so we are commenting this out and using restlets for now
      // const resp = await api.messaging.get(
      //   `NetSuite/Integrations/GetMetadata/${metadataType}`
      // );
      //setMetadata(json.properties);

      if (!metadataType) {
        setMetadata([]);
        return;
      }
      setisLoading(true);
      let metadataToSend = metadataType;
      if (metadataType === "Ticket") {
        metadataToSend = "supportcase";
      }
      if (metadataType === "Order") {
        metadataToSend = "salesorder";
      }

      if (props.selectedConfigId == null) return;
      const endpoint =
        metadataType.toLocaleLowerCase() === "transaction" ||
        metadataType.toLocaleLowerCase() === "products" ||
        metadataType.toLocaleLowerCase() === "returnauthorization"
          ? `NetSuite/Integrations/GetFields/${props.selectedConfigId}`
          : `NetSuite/Integrations/RelayToRestlet/${props.selectedConfigId}`;

      const requestBody =
        metadataType.toLocaleLowerCase() === "transaction" ||
        metadataType.toLocaleLowerCase() === "products" ||
        metadataType.toLocaleLowerCase() === "returnauthorization"
          ? { metadataType }
          : {
              relayType: "fields",
              recordType: [metadataToSend]
            };

      const resp = await api.messaging.post(endpoint, requestBody);
      const json = await resp.json();

      let sortedMetadata;

      if (
        metadataType.toLowerCase() === "transaction" ||
        metadataType.toLowerCase() === "products" ||
        metadataType.toLowerCase() === "returnauthorization"
      ) {
        // Response is a plain array, no need to sort
        sortedMetadata = Array.isArray(json) ? json : [];
      } else {
        // Expecting a response like { salesorder: [...] }
        sortedMetadata = Array.isArray(json[metadataToSend])
          ? json[metadataToSend].sort()
          : [];
      }
      const newOptions = Object.values(sortedMetadata).map((key, i) => ({
        value: key,
        label: key
      })) as Option[];

      if (metadataType === "Estimate") {
        const itemFieldExists = newOptions.some(
          (option) => option.value === "item:items:item:id"
        );
        if (!itemFieldExists) {
          newOptions.push({
            value: "item:items:item:id",
            label: "item:items:item:id"
          });
        }
      }

      if (metadataType === "ReturnAuthorization") {
        const memoFieldExists = newOptions.some(
          (option) => option.value === "memo"
        );
        if (!memoFieldExists) {
          newOptions.push({
            value: "memo",
            label: "memo"
          });
        }
      }

      setMetadata(newOptions);
      setisLoading(false);
    }

    fetchMetadata();
  }, [api.messaging, metadataType, props.selectedConfigId]);

  const addRow = () => {
    const tempId = Math.min(...mappedFields.map(f => f.id), 0) - 1;

    const newMap: NetSuiteMap = {
      id: tempId, 
      netSuiteField: {
        value: "",
        label: ""
      },
      label: "",
      linkedTo: "",
      create: false,
      usedForSearching: false,
      showWhenSelected: false,
      showInSearch: false,
      control: "",
      defaultValue: "",
      recordType: metadataType
    };

    setMappedFields([...mappedFields, newMap]);
  };

  const lastSaved = useRef<NetSuiteMap[] | null>(null);
  const saveTimeout = useRef<NodeJS.Timeout>();
  const isInitialRender = useRef(true);
  const isHydrating = useRef(false);

  const handleDeleteFieldClick = (index: number) => {
    const field = mappedFields[index];
    const fieldName = field.netSuiteField?.label || field.label || "this field";
    setFieldToDelete({ index, fieldName });
    setIsDeleteFieldModalOpen(true);
  };

  const confirmDeleteField = () => {
    if (fieldToDelete === null) return;

    const index = fieldToDelete.index;
    if (mappedFields[index].id !== -1) {
      deleteRecord(mappedFields[index].id);
    }
    const clone = [...mappedFields];
    clone.splice(index, 1);
    setMappedFields(clone);
    setIsDeleteFieldModalOpen(false);
    setFieldToDelete(null);
  };

  const isPersonDefaultValue = mappedFields.find(
    f => f.netSuiteField?.value?.toLowerCase() === "isperson"
  )?.defaultValue;

  const requiredFields = useMemo(() => {
    return getRequiredFields(metadataType, isPersonDefaultValue?.toString());
    // metadataRefreshCounter included so the Sync button triggers a refetch.
  }, [metadataType, isPersonDefaultValue, metadataRefreshCounter]);

  const fetchMapping = useCallback(async () => {
    if (props.selectedConfigId == undefined || metadataType == "") return;

    isHydrating.current = true;
    setIsHydratingState(true);
    const resp = await api.messaging.get(
      `NetSuite/Integrations/Mapping/${metadataType}/${props.selectedConfigId}`
    );

    const itemsToBeListed: NetSuiteMap[] = [];
    const json = await resp.json();

    json.forEach((element: any) => {
      const fieldName = typeof element.netSuiteField === 'string'
        ? element.netSuiteField
        : element.netSuiteField?.value;

      if (fieldName?.toLowerCase() === "isperson" || fieldName?.toLowerCase() === "subsidiary") {
        element.control = "List";
      }

      const value = {
        value: element.netSuiteField,
        label: element.netSuiteField
      };
      element.netSuiteField = value;
    });

    // Remove duplicates from json (case-insensitive)
    const uniqueJson = json.filter(
      (field: NetSuiteMap, index: number, self: any[]) => {
        const fieldValue = field.netSuiteField?.value;
        if (!fieldValue) return true; // Keep empty fields
        const fieldValueLower = fieldValue.toLowerCase();
        // Keep only the first occurrence of each field value (case-insensitive)
        return (
          self.findIndex(
            (f: NetSuiteMap) => f.netSuiteField?.value?.toLowerCase() === fieldValueLower
          ) === index
        );
      }
    );

    const persistedFields = uniqueJson.filter(
      (field: NetSuiteMap) => field.id > 0
    );

    const fieldsToAdd: NetSuiteMap[] = [];
    const fieldsToRemove: number[] = [];

    const isPersonField = uniqueJson.find(
      (field: NetSuiteMap) => field.netSuiteField?.value?.toLowerCase() === "isperson"
    );
    const isPersonValue = isPersonField?.defaultValue?.toString();
    const currentRequiredFields = getRequiredFields(metadataType, isPersonValue);
    const currentRequiredFieldsSet = new Set(currentRequiredFields);

    const possibleRequiredFields = [
      "firstname",
      "lastname",
      "companyname",
      "email"
    ];

    uniqueJson.forEach((field: NetSuiteMap) => {
      const fieldValue = field.netSuiteField?.value?.toLowerCase();
      if (
        fieldValue &&
        possibleRequiredFields.includes(fieldValue) &&
        !currentRequiredFieldsSet.has(fieldValue) &&
        field.create === true &&
        !field.label &&
        !field.linkedTo &&
        !field.defaultValue
      ) {
        fieldsToRemove.push(field.id);
      }
    });

    const filteredJson = uniqueJson.filter(
      (field: NetSuiteMap) => !fieldsToRemove.includes(field.id)
    );

    // Create set of existing field values from filteredJson to prevent duplicates
    const existingFieldValuesSet = new Set(
      filteredJson
        .map((field: NetSuiteMap) => field.netSuiteField?.value?.toLowerCase())
        .filter(Boolean)
    );

    currentRequiredFields.forEach((requiredField) => {
      const requiredFieldLower = requiredField.toLowerCase();
      if (!existingFieldValuesSet.has(requiredFieldLower)) {
        const control =
          requiredFieldLower === "isperson" ||
          requiredFieldLower === "subsidiary"
            ? "List"
            : "";

        const tempId = Math.min(...[...filteredJson, ...fieldsToAdd].map(f => f.id), 0) - 1;

        fieldsToAdd.push({
          id: tempId,
          netSuiteField: {
            value: requiredField,
            label: requiredField
          },
          label: "",
          linkedTo: "",
          create: true,
          usedForSearching: false,
          showWhenSelected: false,
          showInSearch: false,
          control: control,
          defaultValue: "",
          recordType: metadataType
        });
        existingFieldValuesSet.add(requiredFieldLower);
      }
    });

    setMappedFields([...filteredJson, ...fieldsToAdd]);

    // Wait for all deletes to complete before adding new fields
    if (fieldsToRemove.length > 0) {
      console.info(`Automatically removing ${fieldsToRemove.length} obsolete required field(s) due to isperson value change`);

      const deletePromises = fieldsToRemove
        .filter(id => id > 0)
        .map(async (id) => {
          try {
            await api.messaging.delete(
              `NetSuite/Integrations/Mapping/${id}`
            );
          } catch (error) {
            console.error("Error deleting field:", id, error);
          }
        });

      await Promise.all(deletePromises);
    }

    if (fieldsToAdd.length > 0) {
      setTimeout(async () => {
        try {
          const newMappings = fieldsToAdd.map((field) => ({
            netSuiteField: field.netSuiteField?.value,
            id: 0,
            label: field.label,
            linkedTo: field.linkedTo,
            create: field.create,
            usedForSearching: field.usedForSearching,
            showWhenSelected: field.showWhenSelected,
            showInSearch: field.showInSearch,
            control: field.control,
            defaultValue: field.defaultValue,
            recordType: field.recordType
          }));

          await api.messaging.post(
            `NetSuite/Integrations/Mapping/${props.selectedConfigId}`,
            {
              Mappings: newMappings
            }
          );

          const fetchResp = await api.messaging.get(
            `NetSuite/Integrations/Mapping/${metadataType}/${props.selectedConfigId}`
          );
          const updatedMappings = await fetchResp.json();

          updatedMappings.forEach((element: any) => {
            const fieldName = typeof element.netSuiteField === 'string'
              ? element.netSuiteField
              : element.netSuiteField?.value;

            if (fieldName?.toLowerCase() === "isperson" || fieldName?.toLowerCase() === "subsidiary") {
              element.control = "List";
            }

            const value = {
              value: element.netSuiteField,
              label: element.netSuiteField
            };
            element.netSuiteField = value;
          });

          lastSaved.current = updatedMappings;
          setMappedFields(updatedMappings);
        } catch (error) {
          console.error("Error inserting required fields:", error);
          setIsHydratingState(false);
        } finally {
          isHydrating.current = false;
          setIsHydratingState(false);
        }
      }, 1000);
    } else {
      isHydrating.current = false;
      setIsHydratingState(false);
    }
  }, [api.messaging, metadataType, props.selectedConfigId]);

  useEffect(() => {
    if (!metadataType) return;
    fetchMapping();
  }, [fetchMapping]);

  function isOption(value: any): value is Option {
    return value && typeof value === "object" && "value" in value;
  }

  const saveRecord = useCallback(async () => {
    const fieldsToSave = mappedFields.filter((f) => f.id > 0);

    if (fieldsToSave.length === 0) {
      return;
    }

    const newMap: {
      netSuiteField: any;
      id: number;
      label: string;
      linkedTo: string;
      create: boolean;
      usedForSearching: boolean;
      showWhenSelected: boolean;
      showInSearch: boolean;
      control: string;
      defaultValue: string | SingleValue<Option>;
      recordType: string;
    }[] = [];
    fieldsToSave.forEach((element) => {
      newMap.push({
        netSuiteField: element.netSuiteField?.value,
        id: element.id,
        label: element.label,
        linkedTo: element.linkedTo,
        create: element.create,
        usedForSearching: element.usedForSearching,
        showWhenSelected: element.showWhenSelected,
        showInSearch: element.showInSearch,
        control: element.control,
        defaultValue:
          element.defaultValue && isOption(element.defaultValue)
            ? element.defaultValue.value
            : element.defaultValue,
        recordType: element.recordType
      });
    });

    const response = await api.messaging.post(
      `NetSuite/Integrations/Mapping/${props.selectedConfigId}`,
      {
        Mappings: newMap
      }
    );
  }, [mappedFields, props.selectedConfigId, api.messaging]);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }

    if (isHydrating.current) return;

    if (mappedFields.some((f) => f.id < 0)) {
      return;
    }

    if (lastSaved.current === mappedFields) return;

    if (saveTimeout.current) clearTimeout(saveTimeout.current);

    saveTimeout.current = setTimeout(() => {
      saveRecord();
      lastSaved.current = mappedFields;
    }, 300); 
  }, [mappedFields, saveRecord]);

  async function deleteRecord(id: number) {
    const response = await api.messaging.delete(
      `NetSuite/Integrations/Mapping/${id}`
    );
  }

  const handleDeleteRecordClick = (recordType: string) => {
    setRecordTypeToDelete(recordType);
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteRecord = async () => {
    if (!recordTypeToDelete) return;

    const response = await api.messaging.delete(
      `NetSuite/Integrations/Mapping/DeleteRecord/${recordTypeToDelete}`
    );

    const updatedFields = mappedFields.filter(
      (field) => field.recordType !== recordTypeToDelete
    );
    setMappedFields(updatedFields);
    setIsDeleteModalOpen(false);
    setRecordTypeToDelete(null);
  };

  const updateMappedField = async (index: number, mappedField: NetSuiteMap) => {
    const clone = [...mappedFields];

    // Check if the selected field already exists in another row
    const fieldValue = mappedField.netSuiteField?.value;
    if (fieldValue) {
      const duplicateIndex = clone.findIndex(
        (field, idx) =>
          idx !== index && field.netSuiteField?.value === fieldValue
      );

      if (duplicateIndex !== -1) {
        alert(
          `The field "${mappedField.netSuiteField?.label}" is already mapped. Please select a different field.`
        );
        return;
      }
    }

    // Disable usedForSearching for fields with colons to prevent NetSuite errors
    if (fieldValue && fieldValue.includes(':')) {
      mappedField.usedForSearching = false;
    }

    const isIsPersonField = mappedField.netSuiteField?.value?.toLowerCase() === "isperson";
    const oldIsPersonField = clone[index];
    const isPersonValueChanged =
      isIsPersonField &&
      oldIsPersonField?.defaultValue !== mappedField.defaultValue;

    clone[index] = mappedField;

    if (mappedField.id < 0 && mappedField.netSuiteField?.value) {
      const newMapping = {
        netSuiteField: mappedField.netSuiteField?.value,
        id: 0, 
        label: mappedField.label,
        linkedTo: mappedField.linkedTo,
        create: mappedField.create,
        usedForSearching: mappedField.usedForSearching,
        showWhenSelected: mappedField.showWhenSelected,
        showInSearch: mappedField.showInSearch,
        control: mappedField.control,
        defaultValue: isOption(mappedField.defaultValue)
            ? mappedField.defaultValue.value
            : mappedField.defaultValue,
        recordType: mappedField.recordType
      };

      try {
        await api.messaging.post(
          `NetSuite/Integrations/Mapping/${props.selectedConfigId}`,
          {
            Mappings: [newMapping]
          }
        );

        const fetchResp = await api.messaging.get(
          `NetSuite/Integrations/Mapping/${metadataType}/${props.selectedConfigId}`
        );
        const updatedMappings = await fetchResp.json();

        updatedMappings.forEach((element: any) => {
          const fieldName = typeof element.netSuiteField === 'string'
            ? element.netSuiteField
            : element.netSuiteField?.value;

          if (fieldName?.toLowerCase() === "isperson" || fieldName?.toLowerCase() === "subsidiary") {
            element.control = "List";
          }

          const value = {
            value: element.netSuiteField,
            label: element.netSuiteField
          };
          element.netSuiteField = value;
        });

        // Update the state with fresh data from backend
        setMappedFields(updatedMappings);
        lastSaved.current = updatedMappings;
        return;
      } catch (error) {
        console.error("Error inserting new mapping:", error);
      }
    }

    setMappedFields(clone);

    // If isperson value changed, trigger a refresh to update required fields
    if (isPersonValueChanged) {
      setTimeout(async () => {
        await fetchMapping();
      }, 500);
    }
  };

  function handleModal(passedField: NetSuiteMap) {
    setShowModal(true);
    setPassedField(passedField);
  }

  const saveLinkedTo = useCallback(
    (linkedField: NetSuiteMap) => {
      const clone = [...mappedFields];
      clone[linkedField.id] = linkedField;
      setMappedFields(clone);
    },
    [mappedFields]
  );

  const fetchList = useCallback(
    async (passedFields: NetSuiteMap[]) => {
      const validFields = passedFields.filter(
        (field) => field.netSuiteField?.value
      );

      if (validFields.length === 0) return;

      const jsonArray = validFields.map((passedField) => ({
        recordType: passedField.recordType,
        netSuiteField: passedField.netSuiteField?.value
      }));

      if (props.selectedConfigId == null) return;

      const endpoint =
        props.metadataType.toLocaleLowerCase() === "transaction" ||
        props.metadataType.toLocaleLowerCase() === "products" ||
        props.metadataType.toLocaleLowerCase() === "returnauthorization" ||
        props.metadataType.toLocaleLowerCase() === "estimate"
          ? `NetSuite/Integrations/GetFields/${props.selectedConfigId}`
          : `NetSuite/Integrations/RelayToRestlet/${props.selectedConfigId}`;

      const requestBody =
        props.metadataType.toLocaleLowerCase() === "transaction" ||
        props.metadataType.toLocaleLowerCase() === "products" ||
        props.metadataType.toLocaleLowerCase() === "returnauthorization" ||
        props.metadataType.toLocaleLowerCase() === "estimate"
          ? { metadataType: props.metadataType }
          : {
              relayType: "getList",
              fields: jsonArray
            };

      try {
        const resp = await api.messaging.post(endpoint, requestBody);

        if (!resp.ok) {
          console.error("Failed to fetch list items:", resp.status, resp.statusText);
          return;
        }

        const json = await resp.json();
        const clone = [...(listFields || [])];

        validFields.forEach((passedField) => {
          if (
            clone.filter(
              (item) => item.netSuiteField === passedField.netSuiteField?.value
            ).length > 0
          ) {
            clone.splice(
              clone.findIndex(
                (item) => item.netSuiteField === passedField.netSuiteField?.value
              ),
              1
            );
          }
          let recordType = passedField.recordType;
          if (recordType === "Ticket") {
            recordType = "supportcase";
          }
          if (recordType === "Order") {
            recordType = "salesorder";
          }
          const recordTypeData = json[recordType];
          const netSuiteFieldData =
            recordTypeData?.[passedField.netSuiteField?.value];

          if (netSuiteFieldData) {
            const listItems = netSuiteFieldData.map((item: NetSuiteListItem) => ({
              value: item.value,
              label: item.text.replace(/&nbsp;/gi, "")
            }));

            clone.push({
              netSuiteField: passedField.netSuiteField?.value,
              listItems
            });
          }
        });
        setListFields(clone);
      } catch (error) {
        console.error("Error fetching list items:", error);
      }
    },
    [api.messaging, listFields, props.selectedConfigId]
  );

  useEffect(() => {
    if (mappedFields.length === 0 || loading || listFields != null) return;
    const mappedFieldsToBeListed = mappedFields.filter(
      (item) => item.control === "List"
    );
    // fetchList also has a guard, but double-checking here for safety
    if (mappedFieldsToBeListed.length > 0) {
      fetchList(mappedFieldsToBeListed);
    }
  }, [fetchList, listFields, loading, mappedFields]);

  const ready = useMemo(() => !loading, [loading]);
  if (!ready) {
    return <></>;
  }

  const chevronClass = `w-4 h-4 transition-transform${expanded ? " rotate-180" : ""}`;

  return (
    <div className="panel bg-slate-gray-50 rounded-md shadow-md mb-4">
      <div
        className="panel-header flex items-center h-16 px-4 cursor-pointer"
        onClick={() => setExpanded((s) => !s)}
      >
        <div
          className="text-zinc-50 dark:text-white font-medium text-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <CustomSelect
            options={metaDataOptions}
            value={metadataType}
            width="w-72"
            onChange={setMetadataType}
          />
        </div>

        <div className="ml-auto flex items-center gap-3 dark:text-white">
          {metadataType && props.selectedConfigId ? (
            <div
              className="flex items-center gap-2"
              onClick={(e) => e.stopPropagation()}
            >
              {lastSyncedAt && (
                <span className="text-xs text-zinc-300">
                  Synced {lastSyncedAt.toLocaleTimeString()}
                </span>
              )}
              {syncError && (
                <span className="text-xs text-rose-300" title={syncError}>
                  Sync failed
                </span>
              )}
              <button
                type="button"
                onClick={syncFieldsFromNetSuite}
                disabled={syncing}
                className="text-xs px-2 py-1 rounded border border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50 disabled:opacity-60 disabled:cursor-not-allowed"
                title="Force a fresh pull of field definitions from NetSuite (bypasses the 24h cache)"
              >
                {syncing ? "Syncing…" : "Sync from NetSuite"}
              </button>
            </div>
          ) : null}
          <button
            className="h-8 w-8 text-zinc-400"
            onClick={() => setExpanded((s) => !s)}
          >
            <ChevronDown className={chevronClass} />
          </button>
        </div>
      </div>
      {expanded && (
        <div className="panel-body rounded-2xl animated fadeIn dark:border-stone-100 p-4">
          <table className="p-4 w-full bg-white">
            <thead>
              <tr>
                <th className="pb-4 p-2 pl-4 w-80 text-center">
                  Netsuite Field
                </th>
                <th className="pb-4 p-2 w-80 text-center">Input Type</th>
                <th className="pb-4 p-2 w-80 text-center">Default Value</th>
                <th className="pb-4 p-2 w-24 text-center">Create</th>
                <th className="pb-4 p-2 w-24 text-center">
                  Used For Searching
                </th>
                <th className="pb-4 p-2 w-24 text-center">
                  Show When Selected
                </th>
                <th className="pb-4 p-2 w-24 text-center">Show In Search</th>
                <th className="pb-4 p-2 w-24 text-center"></th>
              </tr>
            </thead>
            <tbody>
              {mappedFields.map((mappedField, i) => (
                <NetSuitePanelRow
                  mappedField={mappedField}
                  i={i}
                  key={i}
                  listFields={listFields || []}
                  updateMappedField={updateMappedField}
                  isLoading={isLoading}
                  deleteRow={handleDeleteFieldClick}
                  handleModal={handleModal}
                  metadata={metadata}
                  fetchList={fetchList}
                  saveRecord={saveRecord}
                  showSavedMessage={props.showSavedMessage}
                  metadataType={metadataType}
                  requiredFields={requiredFields}
                  isHydrating={isHydratingState}
                />
              ))}
            </tbody>
          </table>

          {metadataType && (
            <div className="flex justify-end pt-4 gap-3">
              <SecondaryButton
                onClick={() => handleDeleteRecordClick(metadataType)}
                disabled={props.isDeleteDisabled || mappedFields.length === 0}
              >
                Delete record
              </SecondaryButton>
              <SecondaryButton onClick={addRow}>Add field</SecondaryButton>
            </div>
          )}

          {showModal && passedField && (
            <NetSuiteModal
              netSuiteMap={passedField}
              onClose={() => setShowModal(false)}
              saveLinkedField={() => saveLinkedTo}
            />
          )}
        </div>
      )}

      {isDeleteModalOpen && (
        <Modal show={isDeleteModalOpen}>
          <ModalBody>
            <div className="flex items-center mb-6 justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-8 text-[#EE3763]" />
                <div className="text-zinc-800 text-xl font-medium">
                  Delete Record Type
                </div>
              </div>
              <button onClick={() => setIsDeleteModalOpen(false)}>
                <IconClose className="w-8 text-zinc-500" />
              </button>
            </div>

            <div className="text-center text-zinc-800 text-sm font-normal">
              Are you sure you want to delete all field mappings for{" "}
              <strong>{recordTypeToDelete}</strong>? This action cannot be
              undone and will remove all mapped fields for this record type.
            </div>
            <div className="pt-6 flex justify-center gap-4">
              <GhostButton onClick={() => setIsDeleteModalOpen(false)}>Cancel</GhostButton>
              <PrimaryButton onClick={confirmDeleteRecord}>Delete</PrimaryButton>
            </div>
          </ModalBody>
        </Modal>
      )}

      {isDeleteFieldModalOpen && (
        <Modal show={isDeleteFieldModalOpen}>
          <ModalBody>
            <div className="flex items-center mb-6 justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-8 text-[#EE3763]" />
                <div className="text-zinc-800 text-xl font-medium">
                  Delete Field Mapping
                </div>
              </div>
              <button onClick={() => setIsDeleteFieldModalOpen(false)}>
                <IconClose className="w-8 text-zinc-500" />
              </button>
            </div>

            <div className="text-center text-zinc-800 text-sm font-normal">
              Are you sure you want to delete the field mapping for{" "}
              <strong>{fieldToDelete?.fieldName}</strong>? This action cannot be
              undone.
            </div>
            <div className="pt-6 flex justify-center gap-4">
              <GhostButton onClick={() => setIsDeleteFieldModalOpen(false)}>Cancel</GhostButton>
              <PrimaryButton onClick={confirmDeleteField}>Delete</PrimaryButton>
            </div>
          </ModalBody>
        </Modal>
      )}
    </div>
  );
}
